import React, { useState } from "react";
import {
  PREREQUISITES,
  INVESTOR_PROFILES,
  SLEEVE_CATEGORIES,
  SLEEVE_BUDGET,
  cryptoCapDollars,
} from "./data.js";

const STEP = 100;

function formatMoney(n) {
  return Math.round(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function PlanningStage({ onDeploy }) {
  const [checked, setChecked] = useState({});
  const [gateCleared, setGateCleared] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [allocations, setAllocations] = useState(Object.fromEntries(SLEEVE_CATEGORIES.map((c) => [c.id, 0])));

  const allChecked = PREREQUISITES.every((p) => checked[p.id]);
  const cryptoCap = cryptoCapDollars();

  const setAmount = (id, value) => {
    const clamped = id === "crypto" ? Math.min(Math.max(0, value), cryptoCap) : Math.max(0, value);
    setAllocations((prev) => ({ ...prev, [id]: clamped }));
  };

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + v, 0);
  const remaining = SLEEVE_BUDGET - totalAllocated;
  const canDeploy = remaining === 0 && !!profileId;

  const handleDeploy = () => {
    if (!canDeploy) return;
    onDeploy({ allocations, profileId });
  };

  if (!gateCleared) {
    return (
      <div>
        <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
          <span>THE PREREQUISITES GATE</span>
        </div>

        <p className="mb-5 text-xs text-mist/60">
          Before any alternative asset — before gold, crypto, a rental, anything — the boring
          fundamentals have to already be true. Confirm all three to proceed.
        </p>

        <div className="mb-6 space-y-2">
          {PREREQUISITES.map((p) => (
            <button
              key={p.id}
              onClick={() => setChecked((prev) => ({ ...prev, [p.id]: !prev[p.id] }))}
              className={`flex w-full items-center justify-between border-2 px-3 py-3 text-left transition-colors ${
                checked[p.id] ? "border-lime bg-lime/10" : "border-mist/20"
              }`}
            >
              <span className="text-[12px] text-mist/80">{p.label}</span>
              <span className={`font-display text-[9px] ${checked[p.id] ? "text-lime" : "text-mist/30"}`}>
                {checked[p.id] ? "✔" : "—"}
              </span>
            </button>
          ))}
        </div>

        <button
          onClick={() => setGateCleared(true)}
          disabled={!allChecked}
          className="btn-magenta w-full disabled:opacity-30"
        >
          {allChecked ? "GATE CLEARED — CONTINUE ▶" : "CONFIRM ALL THREE TO CONTINUE"}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>BUILD YOUR SLEEVE</span>
        <span>BUDGET {formatMoney(SLEEVE_BUDGET)}</span>
      </div>

      <section className="panel mb-4 p-4">
        <p className="mb-2 font-display text-[10px] text-cyan">INVESTOR PROFILE</p>
        <div className="space-y-2">
          {INVESTOR_PROFILES.map((p) => (
            <button
              key={p.id}
              onClick={() => setProfileId(p.id)}
              className={`w-full border-2 px-3 py-3 text-left text-[11px] transition-colors ${
                profileId === p.id ? "border-cyan bg-cyan/10 text-cyan" : "border-mist/20 text-mist/70 hover:border-cyan"
              }`}
            >
              <span className="block font-display text-[9px] uppercase">{p.label}</span>
              <span className="mt-1 block text-mist/50">{p.description}</span>
            </button>
          ))}
        </div>
      </section>

      <div
        className={`mb-6 border-2 p-4 text-center transition-colors ${
          remaining === 0 ? "border-lime bg-lime/10" : remaining > 0 ? "border-gold bg-gold/10" : "border-danger bg-danger/10"
        }`}
      >
        <p className="label-pixel mb-1 text-mist/60">Amount Remaining</p>
        <p className={`font-display text-xl ${remaining === 0 ? "text-lime" : remaining > 0 ? "text-gold" : "text-danger"}`}>
          {formatMoney(remaining)}
        </p>
      </div>

      <div className="mb-6 space-y-3">
        {SLEEVE_CATEGORIES.map((cat) => {
          const isCrypto = cat.id === "crypto";
          const atCap = isCrypto && allocations.crypto >= cryptoCap;
          return (
            <div key={cat.id} className="panel p-4">
              <div className="mb-1 flex items-center justify-between">
                <span className="font-display text-[10px] text-mist">
                  {cat.icon} {cat.label}
                </span>
                {isCrypto && <span className="text-[10px] text-gold">cap {formatMoney(cryptoCap)}</span>}
              </div>
              <p className="mb-3 text-[11px] text-mist/50">{cat.flavor}</p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setAmount(cat.id, allocations[cat.id] - STEP)}
                  className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist"
                >
                  −
                </button>
                <span className="w-24 text-center font-mono text-sm text-mist">{formatMoney(allocations[cat.id])}</span>
                <button
                  onClick={() => setAmount(cat.id, allocations[cat.id] + STEP)}
                  disabled={atCap}
                  className="h-7 w-7 border-2 border-black bg-panel2 font-display text-xs text-mist disabled:opacity-30"
                >
                  +
                </button>
              </div>
              {atCap && <p className="mt-2 text-center text-[10px] text-gold">At the 5% cap — on purpose.</p>}
            </div>
          );
        })}
      </div>

      <button onClick={handleDeploy} disabled={!canDeploy} className="btn-magenta w-full disabled:opacity-30">
        {canDeploy ? "DEPLOY YOUR SLEEVE ▶" : "FINISH ALLOCATING TO DEPLOY"}
      </button>
    </div>
  );
}
